import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import useSetMailEditor from "@/hooks/useSetMailEditor";
import useSuggestion from "@/hooks/useSuggestion";
import { setMailActionType } from "@/stores/mailStore";
import { useEffect, useRef, useState } from "react";
import MailInput from "../../writeMail/MailInput";
import type { ComposeTab } from "./sidePanes";
import useLiveAssistantMail from "./useLiveAssistantMail";

/**
 * A mail written from an assistant's draft, in its tab: the whole composer, filled with the draft.
 * A new mail opens on the « To » field (the assistant never picks a recipient); a reply takes the
 * recipients and subject of the mail it answers, read live from the list it was shown in.
 */
const ComposePane = ({
    tab,
    onClose,
}: {
    tab: ComposeTab;
    onClose: () => void;
}) => {
    const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
    const [pendingSuggestion, setPendingSuggestion] = useState(false);
    const toInputRef = useRef<HTMLInputElement>(null);
    const { replyTo } = tab;

    const mail = useLiveAssistantMail(replyTo?.listId ?? "", replyTo?.mail);

    // A reply composes in its mode (reply, reply all): the composer reads it from the mail store.
    useEffect(() => {
        if (replyTo) setMailActionType(replyTo.actionType);
    }, [replyTo?.actionType, tab.draft.id]);

    const { editor, suggestionEditor } = useSetMailEditor(setAttachments);
    const {
        acceptOrRefuseSuggestion,
        isLoading,
        isPendingChange,
        makeSuggestion,
    } = useSuggestion({
        editor,
        suggestionEditor,
        setPendingSuggestion,
        mailContent: mail?.message ?? "",
    });

    if (!editor || !suggestionEditor) return null;

    return (
        <MailInput
            acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
            attachments={attachments}
            closeDialog={onClose}
            draft={tab.draft}
            editor={editor}
            emailToReplyToOrForward={mail}
            embedded
            isLoading={isLoading}
            isNewMessage={!replyTo}
            isPendingChange={isPendingChange}
            mailInputExpanded
            makeSuggestion={makeSuggestion}
            pendingSuggestion={pendingSuggestion}
            setAttachments={setAttachments}
            setPendingSuggestion={setPendingSuggestion}
            suggestionEditor={suggestionEditor}
            toInputRef={toInputRef}
        />
    );
};

export default ComposePane;
